import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import './App.css';

// Import All Core Components
import Header from './components/Header/Header';
import Footer from './components/Footer/Footer';
import Hero from './components/Hero/Hero';
import Features from './components/Features/Features';
import About from './components/About/About';
import Services from './components/Services/Services';
import BillingModules from './components/BillingModules/BillingModules';
import Pricing from './components/Pricing/Pricing';
import Testimonials from './components/Testimonials/Testimonials';
import Contact from './components/Contact/Contact';
import Opd      from './components/Landing/Opd';
import Ipd      from './components/Landing/Ipd';
import Lab      from './components/Landing/Lab';
import Pharmacy from './components/Landing/Pharmacy';
import NabhBilling from './components/Landing/NabhBilling';
import ClinicSolution from './components/Landing/ClinicSolution';
import HospitalSolution from './components/Landing/HospitalSolution';
import LabSolution from './components/Landing/LabSolution';
import BestHmsComparison from './components/Landing/BestHmsComparison';
import LegacyHmsComparison from './components/Landing/LegacyHmsComparison';
import TargetedLanding from './components/Landing/TargetedLanding';

// Shared Page Layout Wrapper to maintain standard UX scaffolding
const PageLayout = ({ children }) => (
  <>
    <Header />
    <main>{children}</main>
    <Footer />
  </>
);

// Lean Home — hero, key features, social proof, CTA
const Home = () => (
  <>
    <Hero />
    <Features />
    <Testimonials />
    <Contact />
  </>
);

// Automatically handles scrolling viewport back to top on route transitions
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

function App() {
  return (
    <HelmetProvider>
      <Router>
        <ScrollToTop />
        <div className="App">
          <Routes>
            {/* Homepage */}
            <Route path="/" element={<PageLayout><Home /></PageLayout>} />

            {/* ── Core Pages ── */}
            <Route path="/features"  element={<PageLayout><Features /></PageLayout>} />
            <Route path="/about"     element={<PageLayout><About /></PageLayout>} />
            <Route path="/services"  element={<PageLayout><Services /></PageLayout>} />
            <Route path="/pricing"   element={<PageLayout><Pricing /></PageLayout>} />
            <Route path="/billing"   element={<PageLayout><BillingModules /></PageLayout>} />
            <Route path="/contact"   element={<PageLayout><Contact /></PageLayout>} />

            {/* ── Core Segment-Specific SEO Landing Pages (Tier 1 & Tier 2) ── */}
            <Route path="/nursing-home-software-india"             element={<PageLayout><TargetedLanding segment="nursing-home" /></PageLayout>} />
            <Route path="/diagnostic-centre-software-india"       element={<PageLayout><TargetedLanding segment="diagnostic-centre" /></PageLayout>} />
            <Route path="/emr-software-small-hospital"             element={<PageLayout><TargetedLanding segment="small-hospital-emr" /></PageLayout>} />
            <Route path="/abha-integration-hospital"               element={<PageLayout><TargetedLanding segment="abha-integration" /></PageLayout>} />
            <Route path="/hms-software-with-abha-integration"      element={<PageLayout><TargetedLanding segment="abha-integration" /></PageLayout>} />

            {/* ── Service Sub-Pages ── */}
            <Route path="/services/opd-management-software"      element={<PageLayout><Opd /></PageLayout>} />
            <Route path="/services/ipd-management-software"      element={<PageLayout><Ipd /></PageLayout>} />
            <Route path="/services/lab-management-software"      element={<PageLayout><Lab /></PageLayout>} />
            <Route path="/services/pharmacy-management-software" element={<PageLayout><Pharmacy /></PageLayout>} />

            {/* ── Extended Strategic SaaS Routes ── */}
            <Route path="/features/nabh-medical-billing"          element={<PageLayout><NabhBilling /></PageLayout>} />
            <Route path="/solutions/for-clinics-and-polyclinics"   element={<PageLayout><ClinicSolution /></PageLayout>} />
            <Route path="/solutions/for-multi-specialty-hospitals" element={<PageLayout><HospitalSolution /></PageLayout>} />
            <Route path="/solutions/for-diagnostic-labs"           element={<PageLayout><LabSolution /></PageLayout>} />
            <Route path="/compare/best-hospital-management-software-india" element={<PageLayout><BestHmsComparison /></PageLayout>} />
            <Route path="/compare/mediway-vs-legacy-hms"          element={<PageLayout><LegacyHmsComparison /></PageLayout>} />

            {/* ── Root-level Shortcut Aliases for SEO ── */}
            <Route path="/nabh-medical-billing"                        element={<PageLayout><NabhBilling /></PageLayout>} />
            <Route path="/best-hospital-management-software-india"     element={<PageLayout><BestHmsComparison /></PageLayout>} />
            <Route path="/mediway-vs-legacy-hms"                       element={<PageLayout><LegacyHmsComparison /></PageLayout>} />

            {/* ── SEO Keyword Alias Routes (all map to the closest real page) ── */}
            {/* → Features page */}
            <Route path="/hospital-management-system-features"   element={<PageLayout><Features /></PageLayout>} />
            <Route path="/hospital-management-software"          element={<PageLayout><Features /></PageLayout>} />
            <Route path="/best-hospital-management-system"       element={<PageLayout><Features /></PageLayout>} />
            <Route path="/hospital-management-system"            element={<PageLayout><Features /></PageLayout>} />
            <Route path="/healthcare-management-system"          element={<PageLayout><Features /></PageLayout>} />
            <Route path="/hospital-information-system"           element={<PageLayout><Features /></PageLayout>} />
            <Route path="/hms-software"                          element={<PageLayout><Features /></PageLayout>} />
            <Route path="/cloud-based-hospital-management-system" element={<PageLayout><Features /></PageLayout>} />
            <Route path="/hospital-erp-software"                 element={<PageLayout><Features /></PageLayout>} />
            <Route path="/hospital-administration-software"      element={<PageLayout><Features /></PageLayout>} />
            <Route path="/healthcare-software-india"             element={<PageLayout><Features /></PageLayout>} />
            <Route path="/hospital-software"                     element={<PageLayout><Features /></PageLayout>} />
            {/* → Pricing page */}
            <Route path="/hms-software-pricing"                  element={<PageLayout><Pricing /></PageLayout>} />
            <Route path="/hospital-software-price-india"         element={<PageLayout><Pricing /></PageLayout>} />
            {/* → Services page */}
            <Route path="/medical-billing-software"              element={<PageLayout><Services /></PageLayout>} />
            <Route path="/medical-management-software"           element={<PageLayout><Services /></PageLayout>} />
            <Route path="/hospital-inventory-management-software" element={<PageLayout><Services /></PageLayout>} />
            {/* → OPD page */}
            <Route path="/opd-management-software"               element={<PageLayout><Opd /></PageLayout>} />
            <Route path="/patient-management-system"             element={<PageLayout><Opd /></PageLayout>} />
            <Route path="/patient-registration-system"           element={<PageLayout><Opd /></PageLayout>} />
            <Route path="/appointment-scheduling-software-for-hospitals" element={<PageLayout><Opd /></PageLayout>} />
            
            {/* → Mapped custom segments for high conversion */}
            <Route path="/clinic-management-software"            element={<PageLayout><TargetedLanding segment="small-hospital-emr" /></PageLayout>} />
            <Route path="/clinic-management-system"              element={<PageLayout><TargetedLanding segment="small-hospital-emr" /></PageLayout>} />
            <Route path="/emr-software"                          element={<PageLayout><TargetedLanding segment="small-hospital-emr" /></PageLayout>} />
            <Route path="/electronic-health-records-software"    element={<PageLayout><TargetedLanding segment="small-hospital-emr" /></PageLayout>} />
            
            {/* → IPD / Lab / Pharmacy */}
            <Route path="/ipd-management-software"               element={<PageLayout><Ipd /></PageLayout>} />
            <Route path="/lab-management-software"               element={<PageLayout><Lab /></PageLayout>} />
            <Route path="/pharmacy-management-software"          element={<PageLayout><Pharmacy /></PageLayout>} />

            {/* 404 fallback */}
            <Route path="*" element={<PageLayout><Home /></PageLayout>} />
          </Routes>
        </div>
      </Router>
    </HelmetProvider>
  );
}

export default App;
